import React from 'react';

/**
 * Small inline marks for the email pickers. Brand marks are reduced to a coloured tile with a
 * letter so they read at 20px and need no external assets; generic concepts (SMTP, IMAP, webhook,
 * password, DNS) get simple line icons.
 */
export type EmailIconId =
  | 'smtp'
  | 'sendgrid'
  | 'mailgun'
  | 'postmark'
  | 'resend'
  | 'google'
  | 'microsoft'
  | 'gmail'
  | 'outlook'
  | 'yandex'
  | 'zoho'
  | 'icloud'
  | 'custom'
  | 'imap'
  | 'webhook'
  | 'none'
  | 'password'
  | 'dns'
  | 'inbound'
  | 'outbound'
  | 'brand'
  | 'plain'
  | 'band'
  | 'alignLeft'
  | 'alignCenter'
  | 'alignRight'
  | 'desktop'
  | 'mobile';

interface EmailIconProps {
  id: EmailIconId | string;
  size?: number;
  className?: string;
}

const Tile: React.FC<{ fill: string; letter: string; textFill?: string; size: number; className?: string }> = ({
  fill,
  letter,
  textFill = '#ffffff',
  size,
  className,
}) => (
  <svg className={className} width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" focusable="false">
    <rect x="1" y="1" width="18" height="18" rx="5" fill={fill} />
    <text
      x="10"
      y="14.2"
      textAnchor="middle"
      fontFamily="Inter, system-ui, -apple-system, Segoe UI, sans-serif"
      fontSize="11.5"
      fontWeight="700"
      fill={textFill}
    >
      {letter}
    </text>
  </svg>
);

const Line: React.FC<{ size: number; className?: string; color?: string; children: React.ReactNode }> = ({
  size,
  className,
  color = '#475569',
  children,
}) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    stroke={color}
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

export const EmailIcon: React.FC<EmailIconProps> = ({ id, size = 20, className }) => {
  switch (id) {
    case 'sendgrid':
      return <Tile fill="#1A82E2" letter="SG" size={size} className={className} />;
    case 'mailgun':
      return <Tile fill="#F0432F" letter="M" size={size} className={className} />;
    case 'postmark':
      return <Tile fill="#FFDE00" letter="P" textFill="#1f2937" size={size} className={className} />;
    case 'resend':
      return <Tile fill="#111111" letter="R" size={size} className={className} />;
    case 'google':
    case 'gmail':
      return (
        <svg className={className} width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <rect x="1" y="1" width="18" height="18" rx="5" fill="#ffffff" stroke="#e2e8f0" />
          <path d="M10 4.5a5.5 5.5 0 1 0 5.3 7H10V9.3h7.7c.1.5.1 1 .1 1.5A7.8 7.8 0 1 1 15.5 5.4l-1.9 1.9A5.4 5.4 0 0 0 10 4.5z" fill="#4285F4" />
          <path d="M4.7 7.6l2.2 1.6A5.5 5.5 0 0 1 10 4.5c1.4 0 2.6.5 3.6 1.3l1.9-1.9A8 8 0 0 0 4.7 7.6z" fill="#EA4335" />
          <path d="M10 18a8 8 0 0 0 5.4-2l-2.1-1.7a5.5 5.5 0 0 1-8.4-2.5l-2.2 1.7A8 8 0 0 0 10 18z" fill="#34A853" />
          <path d="M4.9 11.8a5.4 5.4 0 0 1 0-3.6L2.7 6.5a8 8 0 0 0 0 7z" fill="#FBBC05" />
        </svg>
      );
    case 'microsoft':
    case 'outlook':
      return (
        <svg className={className} width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <rect x="1" y="1" width="18" height="18" rx="5" fill="#ffffff" stroke="#e2e8f0" />
          <rect x="4.5" y="4.5" width="5" height="5" fill="#F25022" />
          <rect x="10.5" y="4.5" width="5" height="5" fill="#7FBA00" />
          <rect x="4.5" y="10.5" width="5" height="5" fill="#00A4EF" />
          <rect x="10.5" y="10.5" width="5" height="5" fill="#FFB900" />
        </svg>
      );
    case 'yandex':
      return <Tile fill="#FC3F1D" letter="Y" size={size} className={className} />;
    case 'zoho':
      return <Tile fill="#E42527" letter="Z" size={size} className={className} />;
    case 'icloud':
      return <Tile fill="#3693F3" letter="iC" size={size} className={className} />;
    case 'imap':
    case 'inbound':
      return (
        <Line size={size} className={className}>
          <path d="M3 11.5V15a1.5 1.5 0 0 0 1.5 1.5h11A1.5 1.5 0 0 0 17 15v-3.5" />
          <path d="M3 11.5h4l1 2h4l1-2h4" />
          <path d="M10 3.5v7M7.2 8l2.8 2.8L12.8 8" />
        </Line>
      );
    case 'outbound':
      return (
        <Line size={size} className={className}>
          <path d="M3 11.5V15a1.5 1.5 0 0 0 1.5 1.5h11A1.5 1.5 0 0 0 17 15v-3.5" />
          <path d="M3 11.5h4l1 2h4l1-2h4" />
          <path d="M10 10.5v-7M7.2 6.3L10 3.5l2.8 2.8" />
        </Line>
      );
    case 'webhook':
      return (
        <Line size={size} className={className}>
          <path d="M11.5 3.5l-3 5h3l-3 5" />
          <path d="M5.5 7.5H3.5M16.5 7.5h-2M5.5 12.5H3.5M16.5 12.5h-2" />
        </Line>
      );
    case 'password':
      return (
        <Line size={size} className={className}>
          <circle cx="7" cy="10" r="3.5" />
          <path d="M10.5 10H17M14.5 10v2.5M16.5 10v2" />
        </Line>
      );
    case 'dns':
      return (
        <Line size={size} className={className}>
          <circle cx="10" cy="10" r="7" />
          <path d="M3 10h14M10 3c2.2 2.1 3.3 4.4 3.3 7s-1.1 4.9-3.3 7c-2.2-2.1-3.3-4.4-3.3-7S7.8 5.1 10 3z" />
        </Line>
      );
    case 'brand':
      return (
        <Line size={size} className={className}>
          <path d="M10 2.8a7.2 7.2 0 1 0 0 14.4c1 0 1.5-.7 1.5-1.4 0-.9-.8-1.2-.8-2 0-.8.6-1.3 1.4-1.3h1.8a3.3 3.3 0 0 0 3.3-3.3C17.2 5.6 14 2.8 10 2.8z" />
          <circle cx="6.5" cy="9" r="1" />
          <circle cx="9" cy="6" r="1" />
          <circle cx="12.8" cy="6.8" r="1" />
        </Line>
      );
    case 'plain':
      return (
        <Line size={size} className={className}>
          <rect x="3" y="3" width="14" height="14" rx="2" />
          <path d="M6 6.5h4M6 10h8M6 13h6" />
        </Line>
      );
    case 'band':
      return (
        <Line size={size} className={className}>
          <rect x="3" y="3" width="14" height="14" rx="2" />
          <path d="M3 7.5h14" />
          <path d="M3.5 4h13v3h-13z" fill="#475569" stroke="none" />
          <path d="M6 10.5h8M6 13.5h6" />
        </Line>
      );
    case 'alignLeft':
      return (
        <Line size={size} className={className}>
          <path d="M3.5 5h13M3.5 8.5h8M3.5 12h13M3.5 15.5h8" />
        </Line>
      );
    case 'alignCenter':
      return (
        <Line size={size} className={className}>
          <path d="M3.5 5h13M6 8.5h8M3.5 12h13M6 15.5h8" />
        </Line>
      );
    case 'alignRight':
      return (
        <Line size={size} className={className}>
          <path d="M3.5 5h13M8.5 8.5h8M3.5 12h13M8.5 15.5h8" />
        </Line>
      );
    case 'desktop':
      return (
        <Line size={size} className={className}>
          <rect x="2.5" y="3.5" width="15" height="10" rx="1.5" />
          <path d="M7 16.5h6M10 13.5v3" />
        </Line>
      );
    case 'mobile':
      return (
        <Line size={size} className={className}>
          <rect x="6" y="2.5" width="8" height="15" rx="1.8" />
          <path d="M9 15h2" />
        </Line>
      );
    case 'none':
      return (
        <Line size={size} className={className} color="#94a3b8">
          <circle cx="10" cy="10" r="6.5" />
          <path d="M5.5 14.5l9-9" />
        </Line>
      );
    case 'smtp':
    case 'custom':
    default:
      return (
        <Line size={size} className={className}>
          <rect x="2.5" y="5" width="15" height="10.5" rx="2" />
          <path d="M3 6l7 5 7-5" />
        </Line>
      );
  }
};
